import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useMemo, useRef, useState } from 'react';
import { hasPermission } from '@/types';
import { useNavigate } from 'react-router-dom';
import { ocenaRyzyka as api, intakes as intakesApi, clients as clientsApi } from '@/api/endpoints';
import type { RiskAssessmentSummary, RiskAssessmentStatusCode, RiskLayerCode, RiskMeta, IntakeRiskReadiness, RiskPackageImportResult } from '@/types';
import type { Intake, Client } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { UploadProgressBar } from '@/components/UploadProgress';
import { fmtBytes, validateUploadSize, type UploadProgress } from '@/lib/uploadLimits';
import { useAuth } from '@/auth/AuthContext';

/** Domyślny termin ustawowy dla warstwy — z parametrów metodyczno-prawnych (RiskMethodologyConfig). */
function deadlineForLayer(meta: RiskMeta, layer: RiskLayerCode): string {
  const m = meta.methodology;
  if (!m) return '';
  return layer === 'UJECIE' ? m.deadlineCatchment
    : layer === 'SYSTEM' ? m.deadlineSystem : m.deadlineInstallation;
}

const LAYERS: { code: RiskLayerCode | ''; label: string }[] = [
  { code: '', label: 'Wszystkie warstwy' },
  { code: 'UJECIE', label: 'Obszar zasilania ujęcia (4f)' },
  { code: 'SYSTEM', label: 'System zaopatrzenia w wodę (4g)' },
  { code: 'INSTALACJA', label: 'Wewnętrzny system wodociągowy (4i)' },
];

export function OcenaRyzykaPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = hasPermission(user, 'RISK_CREATE');

  const [items, setItems] = useState<RiskAssessmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [layer, setLayer] = useState<RiskLayerCode | ''>('');
  const [status, setStatus] = useState<RiskAssessmentStatusCode | ''>('');
  // dostawca wody = klient zlecenia; filtr ułatwia wyłuskanie ocen jednego wodociągu (uwaga 3 z 30.07.2026)
  const [clientId, setClientId] = useState('');
  const [clientList, setClientList] = useState<Client[]>([]);
  const [meta, setMeta] = useState<RiskMeta | null>(null);
  const [showNew, setShowNew] = useState(false);

  const reload = () => {
    setLoading(true);
    api.list(status || undefined, layer || undefined, clientId ? Number(clientId) : undefined)
      .then(setItems)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [status, layer, clientId]);
  useEffect(() => { api.meta().then(setMeta).catch(() => {}); }, []);
  useEffect(() => { clientsApi.list().then(setClientList).catch(() => {}); }, []);

  return (
    <div className="max-w-[1300px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Oceny ryzyka</h1>
          <div className="text-ink-400 text-[13px]">Ustawowe oceny ryzyka bezpieczeństwa wody — obszar zasilania ujęcia (4f), system zaopatrzenia w wodę (4g), wewnętrzny system wodociągowy (4i)</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/ocena-ryzyka/przewodnik')} className="btn btn-secondary btn-sm">
            <Icon name="file-text" className="w-3.5 h-3.5" /> Przewodnik
          </button>
          {canCreate && (
            <button onClick={() => setShowNew(true)} className="btn btn-primary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5" /> Nowa ocena
            </button>
          )}
        </div>
      </header>

      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {LAYERS.map(l => (
          <button key={l.code} onClick={() => setLayer(l.code)}
            className={'btn btn-sm ' + (layer === l.code ? 'btn-primary' : 'btn-ghost')}>{l.label}</button>
        ))}
        <div className="w-px h-5 bg-ink-200 mx-1" />
        <div className="w-[220px]">
          <SearchableSelect value={status} onChange={v => setStatus(v as RiskAssessmentStatusCode | '')} className="btn-sm">
            <option value="">Wszystkie statusy</option>
            {(meta?.statuses ?? []).map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </SearchableSelect>
        </div>
        <div className="w-[240px]">
          <SearchableSelect value={clientId} onChange={setClientId} className="btn-sm">
            <option value="">Wszyscy dostawcy wody</option>
            {clientList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </SearchableSelect>
        </div>
      </div>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}
      {loading && <div className="text-ink-400">Ładowanie…</div>}
      {!loading && items.length === 0 && (
        <div className="card"><div className="card-body text-center text-ink-400 py-8">
          Brak ocen ryzyka. {canCreate && 'Utwórz pierwszą przyciskiem „Nowa ocena".'}
          {' '}Pierwszy raz w tym module? Zacznij od <button onClick={() => navigate('/ocena-ryzyka/przewodnik')}
            className="text-water-600 underline underline-offset-2">przewodnika</button>.
        </div></div>
      )}
      {!loading && items.length > 0 && (
        <div className="card"><div className="card-body !p-0">
          <table className="table-hw">
            <thead><tr>
              <th className="w-32">Numer</th>
              <th>Ocena</th>
              <th className="w-40">Powiązane ujęcie</th>
              <th className="w-40">Dostawca wody</th>
              <th className="w-24 text-center">Zagrożenia</th>
              <th className="w-28">Termin</th>
              <th className="w-32">Status</th>
            </tr></thead>
            <tbody>{items.map(r => (
              <tr key={r.id} onClick={() => navigate('/ocena-ryzyka/' + r.id)}>
                <td className="font-mono text-[12px] text-ink-600">
                  {r.number}
                  {r.fromHydronexis && <Icon name="cloud" className="w-3 h-3 ml-1 inline text-ai-500" />}
                </td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <span className={'pill pill-' + r.layerColor}>{r.legalBasis}</span>
                    {r.criticalFlag && <span className="pill pill-red"><span className="pill-dot" />krytyczne</span>}
                  </div>
                  <div className="font-semibold text-ink-900 mt-0.5">{r.title}</div>
                </td>
                {/* kolumna dotyczy POWIĄZANIA z ujęciem — ocena bez ujęcia pokazuje nazwę obiektu wpisaną ręcznie,
                    ale wyszarzoną, żeby nie udawała powiązania (uwaga 2 z 30.07.2026) */}
                <td className="text-[12px] text-ink-600">
                  {r.intakeId
                    ? <span>{r.intakeName}</span>
                    : <span className="text-ink-400">{r.objectName || '— brak powiązania —'}</span>}
                  <div className="text-ink-400">{r.town || ''}</div>
                </td>
                <td className="text-[12px] text-ink-600">{r.clientName || <span className="text-ink-300">—</span>}</td>
                <td className="text-center font-mono tabular-nums">
                  {r.hazardCount}
                  {r.criticalCount > 0 && <span className="text-red-500"> · {r.criticalCount}!</span>}
                </td>
                <td className="text-[12px]">{deadlineCell(r)}</td>
                <td><span className={'pill pill-' + r.statusColor}>{r.statusName}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div></div>
      )}

      {showNew && meta && (
        <NewModal meta={meta} onClose={() => setShowNew(false)} onCreated={id => { setShowNew(false); navigate('/ocena-ryzyka/' + id); }} />
      )}
    </div>
  );
}

function deadlineCell(r: RiskAssessmentSummary) {
  if (!r.deadline) return <span className="text-ink-400">—</span>;
  const d = r.daysToDeadline;
  const cls = d == null ? '' : d < 0 ? 'text-red-500 font-semibold' : d < 60 ? 'text-amber-500' : 'text-ink-600';
  return <span className={cls}>{r.deadline}{d != null && <span className="text-ink-400"> ({d} dni)</span>}</span>;
}

function NewModal({ meta, onClose, onCreated }: { meta: RiskMeta; onClose: () => void; onCreated: (id: number) => void }) {
  const [layer, setLayer] = useState<RiskLayerCode>('UJECIE');
  const [title, setTitle] = useState('');
  const [intakeId, setIntakeId] = useState('');
  const [clientId, setClientId] = useState('');
  const [objectName, setObjectName] = useState('');
  const [town, setTown] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [jcwpd, setJcwpd] = useState('');
  const [deadline, setDeadline] = useState(() => deadlineForLayer(meta, 'UJECIE') || '2026-12-31');
  const [deadlineTouched, setDeadlineTouched] = useState(false);
  const [qual, setQual] = useState('');
  const [intakeList, setIntakeList] = useState<Intake[]>([]);
  const [clientList, setClientList] = useState<Client[]>([]);
  const [readiness, setReadiness] = useState<IntakeRiskReadiness | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Import paczki danych wejściowych ZIP z HydroNexis (CSEU) — alternatywa dla formularza:
  // po wybraniu paczki reszta pól znika (wszystkie dane pochodzą z manifestu paczki).
  const [pkgFile, setPkgFile] = useState<File | null>(null);
  const [pkgProgress, setPkgProgress] = useState<UploadProgress | null>(null);
  const [pkgResult, setPkgResult] = useState<RiskPackageImportResult | null>(null);
  const pkgInputRef = useRef<HTMLInputElement>(null);

  const pickPackage = (files: FileList | null) => {
    setErr(null);
    const f = files && files.length > 0 ? files[0] : null;
    if (!f) { setPkgFile(null); return; }
    const sizeErr = validateUploadSize(f);
    if (sizeErr) { setErr(sizeErr); return; }
    if (!f.name.toLowerCase().endsWith('.zip')) { setErr('Paczka danych wejściowych musi być plikiem ZIP.'); return; }
    setPkgFile(f);
  };

  const importPackage = () => {
    if (!pkgFile) return;
    setBusy(true); setErr(null); setPkgProgress(null);
    api.importPackage(pkgFile, setPkgProgress)
      .then(setPkgResult)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => { setBusy(false); setPkgProgress(null); });
  };

  useEffect(() => {
    intakesApi.list().then(setIntakeList).catch(() => {});
    clientsApi.list().then(setClientList).catch(() => {});
  }, []);

  // #3 — gotowość ujęcia do automatyzacji (odświeżana przy zmianie ujęcia)
  useEffect(() => {
    if (!intakeId) { setReadiness(null); return; }
    setReadinessLoading(true);
    let live = true;
    api.intakeReadiness(Number(intakeId))
      .then(r => { if (live) setReadiness(r); })
      .catch(() => { if (live) setReadiness(null); })
      .finally(() => { if (live) setReadinessLoading(false); });
    return () => { live = false; };
  }, [intakeId]);

  // #2 — termin podpowiadany z parametrów metodyczno-prawnych, dopóki użytkownik go nie zmienił ręcznie
  const changeLayer = (v: RiskLayerCode) => {
    setLayer(v);
    if (!deadlineTouched) { const d = deadlineForLayer(meta, v); if (d) setDeadline(d); }
  };

  const pickIntake = (v: string) => {
    setIntakeId(v);
    const it = intakeList.find(i => String(i.id) === v);
    if (it) {
      if (!objectName) setObjectName(it.name);
      if (!town && it.town) setTown(it.town);
      if (it.client) setClientId(String(it.client.id));
    }
  };

  const layerName = useMemo(() => meta.layers.find(l => l.code === layer)?.name ?? '', [layer, meta]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pkgResult) { onCreated(pkgResult.assessment.id); return; }
    if (pkgFile) { importPackage(); return; }
    setBusy(true); setErr(null);
    api.create({
      layer, title: title || undefined, intakeId: intakeId ? Number(intakeId) : undefined,
      clientId: clientId ? Number(clientId) : undefined, objectName: objectName || undefined,
      town: town || undefined, deadline: deadline || undefined, requiredSignerQualification: qual || undefined,
      latitude: lat ? Number(lat) : undefined, longitude: lng ? Number(lng) : undefined,
      jcwpdCode: jcwpd || undefined,
    }).then(a => onCreated(a.id)).catch(e => { setErr(e?.message ?? String(e)); setBusy(false); });
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { layer, title, intakeId, clientId, objectName, town, lat, lng, jcwpd, deadline, qual, pkgFile }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-[720px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100"><h2 className="text-[15px] font-semibold text-ink-900">Nowa ocena ryzyka</h2></div>
        <div className="p-5 grid gap-3">
          {/* Import paczki danych wejściowych z HydroNexis (CSEU) — wszystkie dane z ZIP-a. */}
          {pkgResult ? (
            <PackageImportSummary result={pkgResult} />
          ) : (
            <div className="rounded border border-ink-100 bg-ink-50/40 p-3 grid gap-2">
              <div className="text-[12px] font-semibold text-ink-700 flex items-center gap-1.5">
                <Icon name="upload" className="w-3.5 h-3.5 text-ink-400" /> Paczka danych wejściowych (ZIP z HydroNexis)
              </div>
              {!pkgFile && (
                <>
                  <div className="text-[11.5px] text-ink-500">
                    Masz paczkę ZIP wyeksportowaną z modułu oceny ryzyka wodociągu (CSEU)? Wgraj ją —
                    ocena utworzy się z danych paczki: warstwa, dostawca, studnie, zagrożenia z mapy,
                    formularz danych wejściowych i załączniki.
                  </div>
                  <div>
                    <input ref={pkgInputRef} type="file" accept=".zip,application/zip" className="hidden"
                      onChange={e => { pickPackage(e.target.files); e.target.value = ''; }} />
                    <button type="button" onClick={() => pkgInputRef.current?.click()} className="btn btn-secondary btn-sm">
                      <Icon name="folder" className="w-3.5 h-3.5" /> Wybierz plik ZIP…
                    </button>
                  </div>
                </>
              )}
              {pkgFile && (
                <>
                  <div className="flex items-center gap-2 text-[12px]">
                    <span className="pill pill-water shrink-0">ZIP</span>
                    <span className="text-ink-800 font-medium truncate min-w-0">{pkgFile.name}</span>
                    <span className="text-ink-400 font-mono text-[11px] whitespace-nowrap">{fmtBytes(pkgFile.size)}</span>
                    {!busy && (
                      <button type="button" onClick={() => setPkgFile(null)}
                        className="btn btn-ghost btn-sm ml-auto shrink-0" title="Usuń wybraną paczkę">
                        <Icon name="x" className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {pkgProgress && (
                    <UploadProgressBar percent={pkgProgress.percent} loadedBytes={pkgProgress.loaded}
                      totalBytes={pkgProgress.total} label="Wysyłanie i import paczki…" compact />
                  )}
                  <div className="text-[11.5px] text-ink-500">
                    Pozostałe pola formularza są pomijane — dane oceny pochodzą w całości z paczki.
                  </div>
                </>
              )}
            </div>
          )}

          {!pkgFile && !pkgResult && (<>
          <label className="field"><span className="field-label">Warstwa ustawowa *</span>
            <SearchableSelect value={layer} onChange={v => changeLayer(v as RiskLayerCode)}>
              {meta.layers.map(l => <option key={l.code} value={l.code}>{l.name} ({l.legalBasis})</option>)}
            </SearchableSelect>
          </label>
          <label className="field"><span className="field-label">Tytuł</span>
            <input value={title} onChange={e => setTitle(e.target.value)} className="input" placeholder={layerName} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Ujęcie (opcjonalnie)</span>
              <SearchableSelect value={intakeId} onChange={pickIntake}>
                <option value="">— brak powiązania —</option>
                {intakeList.map(i => <option key={i.id} value={i.id}>{i.name}{i.number ? ` (${i.number})` : ''}</option>)}
              </SearchableSelect>
            </label>
            <label className="field"><span className="field-label">Dostawca wody (opcjonalnie)</span>
              <SearchableSelect value={clientId} onChange={setClientId}>
                <option value="">— brak —</option>
                {clientList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SearchableSelect>
            </label>
          </div>

          {/* #3 — gotowość danych ujęcia do automatyzacji */}
          {intakeId && <IntakeReadinessPanel readiness={readiness} loading={readinessLoading} />}

          {/* Studnie ujęcia — dane wejściowe obszaru zasilania (art. 4f dotyczy obszaru zasilania
              studni, nie punktu technologicznego). Read-only podgląd; edycja na karcie ujęcia. */}
          {intakeId && (() => {
            const it = intakeList.find(i => String(i.id) === intakeId);
            const bhs = it?.boreholes ?? [];
            return (
              <div className="rounded border border-ink-100 bg-ink-50/40 p-3 grid gap-1.5">
                <div className="text-[12px] font-semibold text-ink-700">Studnie ujęcia ({bhs.length})</div>
                {bhs.length === 0 && <div className="text-[11px] text-ink-400">Ujęcie nie ma jeszcze studni — dodasz je na karcie ujęcia.</div>}
                {bhs.map(b => (
                  <div key={b.id} className="flex items-center gap-2 min-w-0 text-[12px]">
                    <span className="pill pill-water text-[10px] shrink-0">{b.designation}</span>
                    <span className="text-ink-500 truncate min-w-0">{b.functionName ?? ''}</span>
                    <span className="text-ink-400 ml-auto whitespace-nowrap shrink-0 font-mono text-[11px]">
                      {b.latitude != null && b.longitude != null ? `${b.latitude.toFixed(5)}, ${b.longitude.toFixed(5)}` : 'brak współrzędnych'}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Przy powiązanym ujęciu nazwa obiektu i miejscowość idą z ujęcia — nie ma po co pytać
              o nie przy zakładaniu zlecenia (uwaga 22 z 30.07.2026). Bez ujęcia: pola ręczne. */}
          {!intakeId && (
            <div className="grid grid-cols-2 gap-3">
              <label className="field"><span className="field-label">Nazwa obiektu</span>
                <input value={objectName} onChange={e => setObjectName(e.target.value)} className="input" placeholder="np. SUW Reda, studnia 14C" />
              </label>
              <label className="field"><span className="field-label">Miejscowość</span>
                <input value={town} onChange={e => setTown(e.target.value)} className="input" />
              </label>
            </div>
          )}

          {!intakeId && (
            <div className="rounded border border-ink-100 bg-ink-50/40 p-3 grid gap-2">
              <div className="text-[11px] text-ink-500">
                Ocena bez powiązanego ujęcia — wpisz dane ujęcia ręcznie (lub odczytasz je AI z dokumentacji ujęcia w karcie oceny).
                Współrzędne (WGS84) dają marker na mapie; strefę oddziaływania wgrasz jako obszar OSW.
              </div>
              <div className="grid grid-cols-3 gap-3">
                <label className="field"><span className="field-label">Szerokość (lat)</span>
                  <input value={lat} onChange={e => setLat(e.target.value)} className="input" placeholder="np. 54.4600" inputMode="decimal" />
                </label>
                <label className="field"><span className="field-label">Długość (lng)</span>
                  <input value={lng} onChange={e => setLng(e.target.value)} className="input" placeholder="np. 17.0450" inputMode="decimal" />
                </label>
                <label className="field"><span className="field-label">JCWPd (kod)</span>
                  <input value={jcwpd} onChange={e => setJcwpd(e.target.value)} className="input" placeholder="np. PLGW600011" />
                </label>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Termin ustawowy</span>
              <input type="date" value={deadline}
                onChange={e => { setDeadline(e.target.value); setDeadlineTouched(true); }} className="input" />
            </label>
            <label className="field"><span className="field-label">Wymagane uprawnienia podpisu</span>
              <input value={qual} onChange={e => setQual(e.target.value)} className="input" placeholder="np. kat. V / wg rozporządzenia" />
            </label>
          </div>

          {/* #2 — parametry metodyczno-prawne obowiązujące dla tworzonej oceny */}
          <MethodologyPanel meta={meta} />
          </>)}

          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
          <div className="flex justify-end gap-2 mt-1">
            {!pkgResult && (
              <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            )}
            <button type="submit" disabled={busy} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5" />
              {pkgResult ? 'Przejdź do oceny'
                : pkgFile ? (busy ? 'Importowanie…' : 'Importuj paczkę')
                : (busy ? 'Tworzenie…' : 'Utwórz')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

/** Podsumowanie importu paczki CSEU — co powstało, zanim użytkownik przejdzie do oceny. */
function PackageImportSummary({ result }: { result: RiskPackageImportResult }) {
  const a = result.assessment;
  return (
    <div className="rounded border border-green-200 bg-green-50/60 p-3 grid gap-2">
      <div className="text-[12px] font-semibold text-ink-800 flex items-center gap-1.5">
        <Icon name="check" className="w-4 h-4 text-green-600" /> Paczka zaimportowana — utworzono ocenę {a.number}
      </div>
      <div className="text-[12px] text-ink-700">
        <span className={'pill pill-' + a.layerColor}>{a.legalBasis}</span>
        <span className="ml-1.5 font-medium">{a.title}</span>
        {a.clientName && <span className="text-ink-500"> · {a.clientName}</span>}
      </div>
      <ul className="grid gap-0.5 text-[11.5px] text-ink-600">
        <li>Studnie: <b>{result.wellsImported}</b> · Zagrożenia (do weryfikacji): <b>{result.hazardsImported}</b></li>
        <li>Dokumenty źródłowe z paczki: <b>{result.documentsAttached}</b>
          {result.documentsFailed > 0 && <span className="text-red-600"> (niedopięte: {result.documentsFailed})</span>}
        </li>
        <li>Status: <span className={'pill pill-' + a.statusColor}>{a.statusName}</span></li>
      </ul>
      {result.warnings.length > 0 && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 grid gap-0.5">
          <div className="font-semibold">Uwagi importu:</div>
          {result.warnings.map((w, i) => <div key={i}>• {w}</div>)}
        </div>
      )}
    </div>
  );
}

/** #3 Panel gotowości ujęcia do automatycznej oceny (komplet danych wejściowych). */
function IntakeReadinessPanel({ readiness, loading }: { readiness: IntakeRiskReadiness | null; loading: boolean }) {
  if (loading) return <div className="text-[12px] text-ink-400 px-1">Sprawdzanie danych ujęcia…</div>;
  if (!readiness) return null;
  const { present, total, criticalMissing, automationReady, checks } = readiness;
  const pct = total ? Math.round((present / total) * 100) : 0;
  return (
    <div className={'rounded border p-3 ' + (automationReady ? 'border-green-200 bg-green-50/60' : criticalMissing > 0 ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50')}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px] font-semibold text-ink-800 flex items-center gap-1.5">
          <Icon name="shield" className={'w-4 h-4 ' + (automationReady ? 'text-green-600' : criticalMissing > 0 ? 'text-red-500' : 'text-amber-500')} />
          Gotowość danych ujęcia do automatyzacji
        </div>
        <span className="text-[12px] font-mono tabular-nums text-ink-600">{present}/{total} ({pct}%)</span>
      </div>
      <div className="h-1.5 rounded bg-ink-100 overflow-hidden mb-2">
        <div className={'h-full ' + (automationReady ? 'bg-green-500' : criticalMissing > 0 ? 'bg-red-400' : 'bg-amber-400')} style={{ width: pct + '%' }} />
      </div>
      <div className="text-[11.5px] text-ink-600 mb-2">
        {automationReady
          ? 'Ujęcie ma komplet danych krytycznych — ocena wygeneruje się w pełni automatycznie.'
          : criticalMissing > 0
            ? `Brakuje ${criticalMissing} danych krytycznych — automatyczne wyprowadzenie zagrożeń będzie ograniczone (luki trafią do rejestru).`
            : 'Dane krytyczne są komplet — pozostałe braki obniżają jedynie kompletność kontekstu.'}
      </div>
      <ul className="grid gap-1">
        {checks.map(c => (
          <li key={c.key} className="flex items-start gap-1.5 text-[11.5px]" title={c.hint}>
            <Icon name={c.present ? 'check' : 'x'} className={'w-3.5 h-3.5 mt-px shrink-0 ' + (c.present ? 'text-green-600' : c.critical ? 'text-red-500' : 'text-ink-400')} />
            <span className={c.present ? 'text-ink-700' : c.critical ? 'text-red-700 font-medium' : 'text-ink-500'}>
              {c.label}{c.critical && !c.present && <span className="ml-1 text-[10px] uppercase tracking-wide text-red-500">wymagane</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** #2 Parametry metodyczno-prawne (pasma macierzy, terminy, normy) widoczne przy tworzeniu oceny. */
function MethodologyPanel({ meta }: { meta: RiskMeta }) {
  const [open, setOpen] = useState(false);
  const m = meta.methodology;
  if (!m) return null;
  return (
    <div className="rounded border border-ink-100 bg-ink-50/40">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-semibold text-ink-700">
        <span className="flex items-center gap-1.5"><Icon name="settings" className="w-3.5 h-3.5 text-ink-400" /> Parametry metodyczno-prawne oceny</span>
        <Icon name="chevron-down" className={'w-3.5 h-3.5 text-ink-400 transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open && (
        <div className="px-3 pb-3 grid gap-2.5 text-[11.5px] text-ink-600">
          <div>
            <div className="text-ink-500 mb-1">
              Pasma macierzy ryzyka ({m.severityScale === 'KWADRATOWA' ? 'P×D², dotkliwość ważona kwadratowo' : 'P×D'}, skala 1–{m.matrixScaleMax})
            </div>
            <div className="flex flex-wrap gap-1">
              {meta.riskLevels.map(l => (
                <span key={l.code} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-white text-[10.5px] font-medium"
                  style={{ backgroundColor: l.hex }}>
                  {l.name} {l.min}–{l.max}
                </span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Meta label="Termin — obszar zasilania ujęcia (4f)" value={m.deadlineCatchment} />
            <Meta label="Termin — system zaopatrzenia w wodę (4g)" value={m.deadlineSystem} />
            <Meta label="Termin — wewnętrzny system wodociągowy (4i)" value={m.deadlineInstallation} />
            <Meta label="Rewalidacja" value={`co ${m.revalidationYears} lat`} />
            <Meta label="Przekazanie raportu do RZGW" value={m.reportHandoffRequired ? 'wymagane' : 'nie'} />
          </div>
          <Meta label="Katalog norm" value={m.normsCatalog} />
          <Meta label="Lista obserwacyjna" value={m.observationList} />
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-ink-400">{label}:</span> <span className="text-ink-700">{value}</span>
    </div>
  );
}
